import { createFileRoute, useRouterState } from "@tanstack/react-router";
import { ClientAuthProvider } from "@/hooks/useClientAuth";
import { ClientSurfacesProvider } from "@/hooks/useClientSurfaces";
import { ClientContractsProvider } from "@/hooks/useClientContracts";
import { ClientSecurityBoundary } from "@/components/client/ClientLayout";
import { PortalErrorBoundary } from "@/components/PortalErrorBoundary";

// Client portal shell. Invite activation and password reset live OUTSIDE this
// layout (src/routes/client_/) so the magic-link handshake isn't interrupted
// by the provider's session bootstrap.
//   - Mandatory MFA resolves before either business-data provider mounts.
//   - Surface visibility resolves inside the auth provider (it needs the
//     contact) and outside the pages, so the nav and every governed page share it.
//   - Contract scope sits inside surfaces and outside the pages, so the header
//     selector and every page agree on one active contract.
export const Route = createFileRoute("/client")({
  component: ClientPortalLayout,
});

function ClientPortalLayout() {
  // Only committed login/setup/notFound leaves are public. A pending entry
  // URL cannot release the old private outlet or mount its business providers.
  const isPublicEntry = useRouterState({
    select: state => {
      const leaf = state.matches[state.matches.length - 1];
      return leaf?.routeId === "/client/login" || leaf?.routeId === "/client/setup" ||
        (leaf?.routeId === "/client/$" && leaf.status === "notFound");
    },
  });
  return (
    <ClientAuthProvider>
      {/* Setup owns a captured signout/reauthentication transition and its own
          session guard. Keep that exact route mounted throughout the handshake
          so the mandatory-MFA boundary cannot unmount it mid-submit. */}
      {isPublicEntry ? <PortalErrorBoundary /> : (
        <ClientSecurityBoundary>
          <ClientSurfacesProvider>
            <ClientContractsProvider>
              <PortalErrorBoundary />
            </ClientContractsProvider>
          </ClientSurfacesProvider>
        </ClientSecurityBoundary>
      )}
    </ClientAuthProvider>
  );
}
